import React, { useEffect, useState } from "react";
import Header from "../../components/content_wrapper/header";
import { useGetPlans } from "../../hooks/subscriptionManagement/getPlans";
import { useAddPlan } from "../../hooks/subscriptionManagement/addPlan";
import {
  addMonthValidationSchema,
  addPlanValues,
  addTaxValidationSchema,
  addTaxValues,
  combinedValidationSchema,
} from "../../validations/plansValidation";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useToggleDeprecated } from "../../hooks/subscriptionManagement/toggleDeprecated";
import SubscriptionModal from "./subscriptionModal";
import { usePlanStatus } from "../../hooks/subscriptionManagement/planStatus";
import { DeleteSvg } from "../../utils/svgComponent";
import Swal from "sweetalert2";
import { useDeletePlan } from "../../hooks/subscriptionManagement/deletePlan";
import NoRecordFound from "../../components/noRecordFound";
import CustomLaoder from "../../components/customLoader";
import { useAddKey } from "../../hooks/tax_and_stripeKey/addKey";

const SubscriptionManagementScreen: React.FC = () => {
  const [planType, setPlanType] = useState("");
  const [planFilter, setPlanFilter] = useState<"active" | "suspended" | "deprecated" | "all">("active"); // Default to active plans
  const [viewMode, setViewMode] = useState<"card" | "table">("card"); // Default to card view
  const [modalData, setModalData] = useState({
    open: false,
    modalTitle: "",
    modalType: "",
    data: {
      id: "",
      price: "",
      tax: "",
    },
  });

  const { data, isError, isLoading } = useGetPlans();
  const { mutateAsync } = useAddPlan();
  const { mutateAsync: toggleDeprecated } = useToggleDeprecated();
  const { mutateAsync: updateStatus } = usePlanStatus();
  const { mutateAsync: deletePlan } = useDeletePlan();
  const { mutateAsync: addKey } = useAddKey();

  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    const taxNotes = data?.data?.taxNotes ?? data?.data?.tax_notes;
    if (taxNotes !== undefined) {
      setShowNotes(taxNotes);
    }
  }, [data]);
  const _handleCheckboxChange = async () => {
    const newShowNotes = !showNotes;
    setShowNotes(newShowNotes);

    try {
      const res: any = await addKey({
        type: 3,
        external_key: newShowNotes,
      });
      if (res?.status) {
        toast.success(res.message);
      } else {
        toast.error("Failed to update notes.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the notes.");
      console.error("Error updating notes:", error);
    }
  };

  let completeInitialValue;
  let completeInitialSchema;

  if (planType === "month") {
    completeInitialValue = addPlanValues;
    completeInitialSchema = addMonthValidationSchema;
  } else {
    completeInitialValue = addPlanValues;
    completeInitialSchema = combinedValidationSchema;
  }

  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues: _setValues,
    resetForm,
  } = useFormik({
    initialValues: completeInitialValue,
    validationSchema: completeInitialSchema,
    onSubmit: async () => {
      const percentage = data?.data?.taxPercentage ?? data?.data?.tax_percentage ?? 0;
      const totalPriceWithTax = Number(values.price) * (1 - percentage / 100);
      const taxPrice = Number(values.price) * (percentage / 100);

      if (modalData.modalType === "Add") {
        // Handle add submission
        try {
          const res: any = await mutateAsync({
            title: values.title,
            timePeriod: 1, // Always 1 - monthly means per month, yearly means per year
            price: totalPriceWithTax.toFixed(2),
            type: values.type,
            tax_price: taxPrice.toFixed(2),
          });
          
          // Check for success - handle different response structures
          if (res?.status === true || res?.data?.status === true || res?.success === true) {
            const message = res?.message || res?.data?.message || "Plan added successfully!";
            toast.success(message);
            setModalData({ ...modalData, open: false });
            resetForm();
            setPlanType(""); // Reset plan type
          } else {
            // If response doesn't indicate success but no error was thrown
            const errorMsg = res?.message || res?.data?.message || "Failed to add plan. Please try again.";
            toast.error(errorMsg);
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message || error?.message || "An error occurred while adding the plan.";
          toast.error(errorMessage);
        }
      } else {
        return null;
      }
    },
  });

  const {
    values: taxValue,
    touched: taxTouched,
    errors: taxErrors,
    handleBlur: taxHandleBlur,
    handleChange: taxHandleChange,
    handleSubmit: taxHandleSubmit,
    setValues: taxSetValues,
    resetForm: taxReset,
  } = useFormik({
    initialValues: addTaxValues,
    validationSchema: addTaxValidationSchema,
    onSubmit: async () => {
      if (modalData.modalType === "taxEdit") {
        // Handle edit submission
        try {
          const res: any = await addKey({
            type: 1,
            external_key: taxValue.tax,
          });
          if (res?.status === true) {
            toast.success(res?.message);
            setModalData({ ...modalData, open: false });
            taxReset();
          }
        } catch (error: any) {
          toast.error(error?.response?.data?.message);
        }
      } else if (modalData.modalType === "tax") {
        try {
          const res: any = await addKey({
            type: 1,
            external_key: taxValue.tax,
          });
          if (res?.status === true) {
            toast.success(res?.message);
            setModalData({ ...modalData, open: false });
            taxReset();
          }
        } catch (error: any) {
          toast.error(error?.response?.data?.message);
        }
      } else {
        return null;
      }
    },
  });

  const percentage = data?.data?.taxPercentage ?? data?.data?.tax_percentage ?? 0;
  const totalPriceWithTax = Number(values.price) * (1 - percentage / 100);
  const handleModalOpen = (e: any, data?: any) => {
    if (e.target.title === "tax") {
      setModalData({
        ...modalData,
        modalTitle: "Add Tax",
        open: true,
        modalType: e.target.title,
      });
    }
    if (e.target.title === "taxEdit") {
      setModalData({
        ...modalData,
        modalTitle: "Edit Tax",
        open: true,
        modalType: e.target.title,
        data: { id: data?._id, tax: data, price: "" },
      });
      taxSetValues({
        tax: data,
      });
    }
    if (e.target.title === "Add") {
      setModalData({
        ...modalData,
        modalTitle: "Add Plan",
        open: true,
        modalType: e.target.title,
        // data: { id: "", price: "" },
      });
    }
    return null;
  };

  const handleCloseModal = () => {
    setModalData({
      ...modalData,
      modalTitle: "",
      open: false,
    });
    resetForm();
    taxReset();
  };

  const returnChild = () => {
    if (modalData.modalType === "tax") {
      return (
        <div className="common-modal-form" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2c3e50"
              }}>📊</span>
              <label style={{
                fontWeight: "600",
                marginBottom: "0",
                fontSize: "14px",
                color: "#2c3e50"
              }}>
                Tax Percentage
              </label>
            </div>
            <p style={{
              fontSize: "13px",
              color: "#6c757d",
              marginBottom: "12px",
              marginTop: "4px"
            }}>
              Enter the tax percentage to be applied to all subscription plans (e.g., 20 for 20%)
            </p>
            <div style={{ position: "relative" }}>
              <input
                placeholder="e.g., 20"
                className="form-control"
                type="number"
                name="tax"
                min="0"
                max="99"
                step="0.01"
                onChange={taxHandleChange}
                value={taxValue.tax}
                onBlur={taxHandleBlur}
                style={{
                  padding: "10px 36px 10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #ced4da",
                  fontSize: "14px"
                }}
              />
              <span style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                fontWeight: "600",
                color: "#6c757d"
              }}>%</span>
            </div>
            {taxTouched.tax && taxErrors.tax && (
              <p className="error" style={{ marginTop: "6px", marginBottom: "0" }}>{taxErrors.tax}</p>
            )}
            {taxValue.tax && Number(taxValue.tax) > 0 && (
              <div style={{
                marginTop: "12px",
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "6px",
                border: "1px solid #ffc107"
              }}>
                <p style={{ fontSize: "13px", color: "#856404", marginBottom: "0" }}>
                  ℹ️ This tax rate will be applied to all new and existing subscription plans
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }
    if (modalData.modalType === "taxEdit") {
      return (
        <div className="common-modal-form" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2c3e50"
              }}>📊</span>
              <label style={{
                fontWeight: "600",
                marginBottom: "0",
                fontSize: "14px",
                color: "#2c3e50"
              }}>
                Update Tax Percentage
              </label>
            </div>
            <p style={{
              fontSize: "13px",
              color: "#6c757d",
              marginBottom: "12px",
              marginTop: "4px"
            }}>
              Modify the tax percentage applied to all subscription plans
            </p>
            <div style={{ position: "relative" }}>
              <input
                type="number"
                name="tax"
                min="0"
                max="99"
                step="0.01"
                className="form-control"
                placeholder="e.g., 20"
                value={taxValue.tax}
                onChange={taxHandleChange}
                onBlur={taxHandleBlur}
                style={{
                  padding: "10px 36px 10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #ced4da",
                  fontSize: "14px"
                }}
              />
              <span style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                fontWeight: "600",
                color: "#6c757d"
              }}>%</span>
            </div>
            {taxTouched.tax && taxErrors.tax && (
              <p className="error" style={{ marginTop: "6px", marginBottom: "0" }}>{taxErrors.tax}</p>
            )}
            {taxValue.tax && Number(taxValue.tax) > 0 && (
              <div style={{
                marginTop: "12px",
                padding: "12px",
                backgroundColor: "#fff3cd",
                borderRadius: "6px",
                border: "1px solid #ffc107"
              }}>
                <p style={{ fontSize: "13px", color: "#856404", marginBottom: "0" }}>
                  ⚠️ Changing the tax rate will affect all future transactions
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }
    if (modalData.modalType === "Add") {
      return (
        <div className="common-modal-form" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Plan Type Section */}
          <div style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2c3e50"
              }}>📅</span>
              <label style={{
                fontWeight: "600",
                marginBottom: "0",
                fontSize: "14px",
                color: "#2c3e50"
              }}>
                Plan Duration
              </label>
            </div>
            <p style={{
              fontSize: "13px",
              color: "#6c757d",
              marginBottom: "12px",
              marginTop: "4px"
            }}>
              Choose whether this is a monthly or yearly subscription plan
            </p>
            <select
              name="type"
              className="form-select"
              onChange={(e) => {
                handleChange(e);
                setPlanType(e.target.value);
              }}
              value={values.type}
              onBlur={handleBlur}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #ced4da",
                fontSize: "14px"
              }}>
              <option value="" disabled>
                Select billing period
              </option>
              <option value="month">Monthly Plan</option>
              <option value="year">Yearly Plan</option>
            </select>
            {touched.type && errors.type && (
              <p className="error" style={{ marginTop: "6px", marginBottom: "0" }}>{errors.type}</p>
            )}
          </div>

          {/* Plan Name Section */}
          <div style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2c3e50"
              }}>✏️</span>
              <label style={{
                fontWeight: "600",
                marginBottom: "0",
                fontSize: "14px",
                color: "#2c3e50"
              }}>
                Plan Name
              </label>
            </div>
            <p style={{
              fontSize: "13px",
              color: "#6c757d",
              marginBottom: "12px",
              marginTop: "4px"
            }}>
              Give your plan a descriptive name (e.g., "Basic Plan", "Premium Annual")
            </p>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., Premium Plan"
              name="title"
              onChange={handleChange}
              value={values.title}
              onBlur={handleBlur}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #ced4da",
                fontSize: "14px"
              }}
            />
            {touched.title && errors.title && (
              <p className="error" style={{ marginTop: "6px", marginBottom: "0" }}>{errors.title}</p>
            )}
          </div>

          {/* Price Section */}
          <div style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2c3e50"
              }}>💰</span>
              <label style={{
                fontWeight: "600",
                marginBottom: "0",
                fontSize: "14px",
                color: "#2c3e50"
              }}>
                Price (Including Tax)
              </label>
            </div>
            <p style={{
              fontSize: "13px",
              color: "#6c757d",
              marginBottom: "12px",
              marginTop: "4px"
            }}>
              Enter the total price customers will pay (tax will be calculated automatically)
            </p>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                fontWeight: "600",
                color: "#6c757d"
              }}>£</span>
              <input
                type="number"
                min="1"
                max="999"
                className="form-control"
                placeholder="0.00"
                name="price"
                value={values.price}
                onChange={handleChange}
                onBlur={handleBlur}
                style={{
                  padding: "10px 12px 10px 28px",
                  borderRadius: "6px",
                  border: "1px solid #ced4da",
                  fontSize: "14px"
                }}
              />
            </div>
            {touched.price && errors.price && (
              <p className="error" style={{ marginTop: "6px", marginBottom: "0" }}>{errors.price}</p>
            )}
            {values.price && Number(values.price) > 0 && (
              <div style={{
                marginTop: "12px",
                padding: "12px",
                backgroundColor: "#e7f3ff",
                borderRadius: "6px",
                border: "1px solid #b3d9ff"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", color: "#495057" }}>Customer pays:</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#2c3e50" }}>£{Number(values.price).toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", color: "#495057" }}>Tax ({percentage}%):</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#dc3545" }}>-£{(Number(values.price) * (percentage / 100)).toFixed(2)}</span>
                </div>
                <div style={{
                  borderTop: "1px solid #b3d9ff",
                  paddingTop: "6px",
                  marginTop: "6px",
                  display: "flex",
                  justifyContent: "space-between"
                }}>
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#2c3e50" }}>You receive:</span>
                  <span style={{ fontSize: "14px", fontWeight: "700", color: "#28a745" }}>£{totalPriceWithTax.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
  };
  // update status
  const handleToggleClick = async (id: string, action: any) => {
    let Status = action === true ? false : true;
    try {
      await updateStatus({
        plan_id: id,
        plan_status: Status,
      });
    } catch (error: any) {
      console.error("Unexpected error:", error);
    }
  };
  
  // deprecate plan (permanent action)
  const handleDeprecatePlan = async (id: string) => {
    try {
      const res: any = await toggleDeprecated({
        planId: id,
        deprecated: true,
      });
      if (res?.status === true) {
        toast.success(res?.message);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to deprecate plan");
    }
  };
  
  // delete Plan with comprehensive safety checks
  const handleDeleteCoupon = (id: any) => {
    Swal.fire({
      title: "Delete Subscription Plan?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 12px;">⚠️ <strong>This action cannot be undone.</strong></p>
          <p style="margin-bottom: 8px; font-size: 14px;">The system will verify:</p>
          <ul style="text-align: left; font-size: 14px; color: #666; margin-left: 20px;">
            <li>No keys (past or present) reference this plan</li>
            <li>No transaction records exist in database</li>
            <li>No subscriptions exist in Stripe</li>
            <li>Plan is not marked as deprecated</li>
          </ul>
          <p style="margin-top: 12px; font-size: 13px; color: #856404; background: #fff3cd; padding: 8px; border-radius: 4px;">
            💡 <strong>Tip:</strong> If the plan has any history, use "Deprecate" instead to phase it out while preserving data integrity.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete Plan",
      cancelButtonText: "Cancel",
      width: "600px"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res: any = await deletePlan({ plan_id: id });
          
          // Check if deletion was successful
          if (res?.status === true || res?.data?.status === true) {
            toast.success(res?.message || res?.data?.message || "Plan deleted successfully");
          } else {
            // Handle validation errors from backend
            const errorMsg = res?.message || res?.data?.message || "Failed to delete plan";
            toast.error(errorMsg);
          }
        } catch (error: any) {
          // Handle error responses with detailed messages
          const errorData = error?.response?.data;
          const errorMessage = errorData?.message || error?.message || "An error occurred while deleting the plan";
          
          // Show detailed error with reason if available
          if (errorData?.data?.reason) {
            const reason = errorData.data.reason;
            const details = errorData.data;
            
            let detailedMessage = errorMessage;
            
            if (reason === "deprecated_plan") {
              detailedMessage = `Cannot delete deprecated plan "${details.plan_title}". Deprecated plans must be retained for historical data integrity.`;
            } else if (reason === "subscription_history") {
              // Enhanced message with breakdown
              const activeCount = details.active_count || 0;
              const cancelledCount = details.cancelled_count || 0;
              const totalCount = details.keys_count || 0;
              
              if (activeCount > 0) {
                detailedMessage = `Cannot delete plan with ${totalCount} key${totalCount > 1 ? 's' : ''} (${activeCount} active, ${cancelledCount} cancelled). This would corrupt historical data and break key references.`;
              } else {
                detailedMessage = `Cannot delete plan with ${totalCount} key${totalCount > 1 ? 's' : ''} in history (all cancelled/expired). This would still corrupt historical data.`;
              }
            } else if (reason === "transaction_history") {
              detailedMessage = `Cannot delete plan with ${details.transaction_count} historical transaction${details.transaction_count > 1 ? 's' : ''}. This would corrupt financial records.`;
            } else if (reason === "stripe_subscriptions_exist") {
              detailedMessage = `Cannot delete plan - Stripe records show subscriptions exist. This indicates data inconsistency. Please deprecate instead and contact support.`;
            }
            
            toast.error(detailedMessage, { autoClose: 10000 });
          } else {
            toast.error(errorMessage, { autoClose: 5000 });
          }
        }
      }
    });
  };
  return (
    <div className="main-wrapper">
      <Header title="Subscriptions" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section profile-section spacer">
            <div className="container">
              <div className="row gy-3">
                <div className="col-xl-12">
                  {/* Help Section */}
                  <div style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "24px",
                    border: "1px solid #e9ecef"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px"
                    }}>
                      <span style={{ fontSize: "24px" }}>ℹ️</span>
                      <h3 style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#2c3e50"
                      }}>
                        Plan Status Guide
                      </h3>
                    </div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: "16px"
                    }}>
                      {/* Suspend Info */}
                      <div style={{
                        backgroundColor: "#ffffff",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6"
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px"
                        }}>
                          <span style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#f8d7da",
                            color: "#721c24"
                          }}>
                            Suspended
                          </span>
                          <span style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#2c3e50"
                          }}>
                            Temporary Hold
                          </span>
                        </div>
                        <p style={{
                          fontSize: "13px",
                          color: "#6c757d",
                          marginBottom: "8px",
                          lineHeight: "1.5"
                        }}>
                          <strong>Use for:</strong> Emergency stops, testing, or temporary unavailability
                        </p>
                        <p style={{
                          fontSize: "13px",
                          color: "#6c757d",
                          marginBottom: "0",
                          lineHeight: "1.5"
                        }}>
                          <strong>Effect:</strong> New users can't subscribe. Existing subscribers continue. Can be reactivated anytime.
                        </p>
                      </div>

                      {/* Deprecate Info */}
                      <div style={{
                        backgroundColor: "#ffffff",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6"
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px"
                        }}>
                          <span style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#fff3cd",
                            color: "#856404"
                          }}>
                            Deprecated
                          </span>
                          <span style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#2c3e50"
                          }}>
                            Permanent Phase-Out
                          </span>
                        </div>
                        <p style={{
                          fontSize: "13px",
                          color: "#6c757d",
                          marginBottom: "8px",
                          lineHeight: "1.5"
                        }}>
                          <strong>Use for:</strong> Replacing with new pricing, discontinuing plan permanently
                        </p>
                        <p style={{
                          fontSize: "13px",
                          color: "#6c757d",
                          marginBottom: "0",
                          lineHeight: "1.5"
                        }}>
                          <strong>Effect:</strong> New users can't subscribe. Existing subscribers keep their plan (grandfathered). Intended to be permanent.
                        </p>
                      </div>

                      {/* Best Practice */}
                      <div style={{
                        backgroundColor: "#e7f3ff",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid #b3d9ff"
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px"
                        }}>
                          <span style={{ fontSize: "16px" }}>💡</span>
                          <span style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#2c3e50"
                          }}>
                            Best Practice
                          </span>
                        </div>
                        <p style={{
                          fontSize: "13px",
                          color: "#495057",
                          marginBottom: "8px",
                          lineHeight: "1.5"
                        }}>
                          <strong>To change prices:</strong> Deprecate the old plan and create a new one. This keeps existing customers at their current price while new customers pay the new price.
                        </p>
                        <p style={{
                          fontSize: "13px",
                          color: "#495057",
                          marginBottom: "0",
                          lineHeight: "1.5"
                        }}>
                          <strong>Note:</strong> You cannot edit plan prices. This prevents billing confusion and maintains Stripe compliance.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="addsub-plan gap-3 align-content-center">
                    <button
                      title="Add"
                      className="btn primary-btn me-0"
                      onClick={(e) => handleModalOpen(e)}>
                      + Add New Plan
                    </button>
                  </div>
                  
                  {/* Filter Tabs and View Toggle */}
                  <div style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    display: "flex",
                    gap: "12px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}>
                    <div style={{
                      display: "flex",
                      gap: "12px",
                      flexWrap: "wrap",
                      alignItems: "center"
                    }}>
                      <span style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#6c757d",
                        marginRight: "8px"
                      }}>
                        Filter:
                      </span>
                    {[
                      { key: "active", label: "Active Plans", icon: "✓" },
                      { key: "suspended", label: "Suspended Plans", icon: "⏸️" },
                      { key: "deprecated", label: "Deprecated Plans", icon: "⚠️" },
                      { key: "all", label: "All Plans", icon: "📋" }
                    ].map((filter) => {
                      const plans = data?.data?.subscriptionPlans || data?.data?.subscription_plans || [];
                      const count = filter.key === "active"
                        ? plans.filter((p: any) => !(p.deprecated ?? false) && !(p.suspended ?? false)).length
                        : filter.key === "suspended"
                        ? plans.filter((p: any) => (p.suspended ?? false) && !(p.deprecated ?? false)).length
                        : filter.key === "deprecated"
                        ? plans.filter((p: any) => (p.deprecated ?? false)).length
                        : plans.length;
                      
                      const isActive = planFilter === filter.key;
                      
                      return (
                        <button
                          key={filter.key}
                          onClick={() => setPlanFilter(filter.key as "active" | "deprecated" | "all")}
                          style={{
                            padding: "10px 20px",
                            backgroundColor: isActive ? "#72bcb8" : "#ffffff",
                            color: isActive ? "#ffffff" : "#2c3e50",
                            border: `2px solid ${isActive ? "#72bcb8" : "#e9ecef"}`,
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.borderColor = "#72bcb8";
                              e.currentTarget.style.backgroundColor = "#f8f9fa";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.borderColor = "#e9ecef";
                              e.currentTarget.style.backgroundColor = "#ffffff";
                            }
                          }}
                        >
                          <span>{filter.icon}</span>
                          <span>{filter.label}</span>
                          <span style={{
                            padding: "2px 8px",
                            backgroundColor: isActive ? "rgba(255, 255, 255, 0.2)" : "#e9ecef",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "700"
                          }}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                    </div>
                    
                    {/* View Mode Toggle */}
                    <div style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center"
                    }}>
                      <span style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#6c757d",
                        marginRight: "4px"
                      }}>
                        View:
                      </span>
                      <button
                        onClick={() => setViewMode("card")}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: viewMode === "card" ? "#72bcb8" : "#ffffff",
                          color: viewMode === "card" ? "#ffffff" : "#2c3e50",
                          border: `2px solid ${viewMode === "card" ? "#72bcb8" : "#e9ecef"}`,
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}
                        title="Card View"
                      >
                        <span>🎴</span>
                        <span>Cards</span>
                      </button>
                      <button
                        onClick={() => setViewMode("table")}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: viewMode === "table" ? "#72bcb8" : "#ffffff",
                          color: viewMode === "table" ? "#ffffff" : "#2c3e50",
                          border: `2px solid ${viewMode === "table" ? "#72bcb8" : "#e9ecef"}`,
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}
                        title="Table View"
                      >
                        <span>📊</span>
                        <span>Table</span>
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: "24px" }}>
                    {viewMode === "table" ? (
                      /* Table View */
                      <div style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                        border: "1px solid #e9ecef"
                      }}>
                        <table style={{
                          width: "100%",
                          borderCollapse: "collapse"
                        }}>
                          <thead>
                            <tr style={{
                              backgroundColor: "#f8f9fa",
                              borderBottom: "2px solid #e9ecef"
                            }}>
                              <th style={{
                                padding: "16px",
                                textAlign: "left",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#2c3e50",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                              }}>Plan Name</th>
                              <th style={{
                                padding: "16px",
                                textAlign: "left",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#2c3e50",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                              }}>Duration</th>
                              <th style={{
                                padding: "16px",
                                textAlign: "right",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#2c3e50",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                              }}>Price</th>
                              <th style={{
                                padding: "16px",
                                textAlign: "right",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#2c3e50",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                              }}>Tax</th>
                              <th style={{
                                padding: "16px",
                                textAlign: "center",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#2c3e50",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                              }}>Status</th>
                              <th style={{
                                padding: "16px",
                                textAlign: "center",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#2c3e50",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                              }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.data?.subscriptionPlans || data?.data?.subscription_plans)
                              ?.filter((items: any) => {
                                const isDeprecated = items.deprecated ?? false;
                                const isSuspended = items.suspended ?? false;
                                
                                if (planFilter === "active") return !isDeprecated && !isSuspended;
                                if (planFilter === "suspended") return isSuspended && !isDeprecated;
                                if (planFilter === "deprecated") return isDeprecated;
                                return true;
                              })
                              ?.map((items: any, index: number) => {
                                const planId = items.Id ?? items.id ?? items._id;
                                const timePeriod = items.timePeriod ?? items.time_period ?? null;
                                const totalPrice = items.totalPrice ?? items.total_price ?? "0.00";
                                const taxPrice = items.taxPrice ?? items.tax_price ?? "0.00";
                                const planType = items.type ?? "month";
                                const planTitle = items.title ?? "Untitled Plan";
                                const isSuspended = items.suspended ?? false;
                                const isDeprecated = items.deprecated ?? false;
                                
                                const planDuration = timePeriod
                                  ? `${timePeriod} ${planType?.charAt(0).toUpperCase() + planType?.slice(1)}${Number(timePeriod) > 1 ? 's' : ''}`
                                  : planType?.charAt(0).toUpperCase() + planType?.slice(1);
                                
                                return (
                                  <tr key={index} style={{
                                    borderBottom: "1px solid #e9ecef",
                                    transition: "background-color 0.2s"
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                                    <td style={{
                                      padding: "16px",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      color: "#2c3e50"
                                    }}>{planTitle}</td>
                                    <td style={{
                                      padding: "16px",
                                      fontSize: "14px",
                                      color: "#6c757d"
                                    }}>{planDuration}</td>
                                    <td style={{
                                      padding: "16px",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      color: "#72bcb8",
                                      textAlign: "right"
                                    }}>£{totalPrice}</td>
                                    <td style={{
                                      padding: "16px",
                                      fontSize: "14px",
                                      color: "#6c757d",
                                      textAlign: "right"
                                    }}>£{taxPrice}</td>
                                    <td style={{
                                      padding: "16px",
                                      textAlign: "center"
                                    }}>
                                      {isDeprecated ? (
                                        <span style={{
                                          padding: "4px 12px",
                                          borderRadius: "12px",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          backgroundColor: "#fff3cd",
                                          color: "#856404",
                                          display: "inline-block"
                                        }}>
                                          Deprecated
                                        </span>
                                      ) : (
                                        <span style={{
                                          padding: "4px 12px",
                                          borderRadius: "12px",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          backgroundColor: isSuspended === false ? "#d4edda" : "#f8d7da",
                                          color: isSuspended === false ? "#155724" : "#721c24",
                                          display: "inline-block"
                                        }}>
                                          {isSuspended === false ? "Active" : "Suspended"}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{
                                      padding: "16px",
                                      textAlign: "center"
                                    }}>
                                      <div style={{
                                        display: "flex",
                                        gap: "8px",
                                        justifyContent: "center",
                                        alignItems: "center"
                                      }}>
                                        {!isDeprecated && (
                                          <>
                                            <button
                                              onClick={() => handleToggleClick(planId, items?.suspended)}
                                              style={{
                                                padding: "6px 12px",
                                                backgroundColor: items?.suspended === false ? "#6c757d" : "#72bcb8",
                                                color: "#ffffff",
                                                border: "none",
                                                borderRadius: "6px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                cursor: "pointer",
                                                transition: "opacity 0.2s"
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                                              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                                              title={items?.suspended === false ? "Suspend plan" : "Activate plan"}>
                                              {items?.suspended === false ? "Suspend" : "Activate"}
                                            </button>
                                            <button
                                              onClick={() => handleDeprecatePlan(planId)}
                                              disabled={isSuspended}
                                              style={{
                                                padding: "6px 12px",
                                                backgroundColor: isSuspended ? "#e9ecef" : "#ffc107",
                                                color: isSuspended ? "#6c757d" : "#ffffff",
                                                border: "none",
                                                borderRadius: "6px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                cursor: isSuspended ? "not-allowed" : "pointer",
                                                opacity: isSuspended ? 0.6 : 1,
                                                transition: "opacity 0.2s"
                                              }}
                                              onMouseEnter={(e) => {
                                                if (!isSuspended) e.currentTarget.style.opacity = "0.8";
                                              }}
                                              onMouseLeave={(e) => {
                                                if (!isSuspended) e.currentTarget.style.opacity = "1";
                                              }}
                                              title={isSuspended ? "Activate the plan first before deprecating" : "Deprecate this plan permanently"}>
                                              Deprecate
                                            </button>
                                            <button
                                              onClick={() => handleDeleteCoupon(planId)}
                                              style={{
                                                padding: "6px 10px",
                                                backgroundColor: "transparent",
                                                border: "none",
                                                cursor: "pointer",
                                                borderRadius: "6px",
                                                transition: "background-color 0.2s"
                                              }}
                                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fee"}
                                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                              title="Delete Plan">
                                              <DeleteSvg color="#dc3545" />
                                            </button>
                                          </>
                                        )}
                                        {isDeprecated && (
                                          <span style={{
                                            fontSize: "12px",
                                            color: "#856404",
                                            fontStyle: "italic"
                                          }}>
                                            No actions available
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      /* Card View */
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: "24px",
                        padding: "8px"
                      }}>
                        {(data?.data?.subscriptionPlans || data?.data?.subscription_plans)
                          ?.filter((items: any) => {
                            const isDeprecated = items.deprecated ?? false;
                            const isSuspended = items.suspended ?? false;
                            
                            if (planFilter === "active") return !isDeprecated && !isSuspended;
                            if (planFilter === "suspended") return isSuspended && !isDeprecated;
                            if (planFilter === "deprecated") return isDeprecated;
                            return true; // "all" shows everything
                          })
                          ?.map(
                        (items: any, index: number) => {
                          // Handle both camelCase and snake_case for ID field
                          // Note: The transformer capitalizes _id to Id (capital I)
                          const planId = items.Id ?? items.id ?? items._id;
                          
                          // Handle both camelCase and snake_case
                          const timePeriod = items.timePeriod ?? items.time_period ?? null;
                          const totalPrice = items.totalPrice ?? items.total_price ?? "0.00";
                          const taxPrice = items.taxPrice ?? items.tax_price ?? "0.00";
                          const basePrice = items.price ?? "0.00";
                          const planType = items.type ?? "month";
                          const planTitle = items.title ?? "Untitled Plan";
                          const isSuspended = items.suspended ?? false;
                          const isDeprecated = items.deprecated ?? false;
                          
                          const planDuration = timePeriod
                            ? `${timePeriod} ${planType?.charAt(0).toUpperCase() + planType?.slice(1)}${Number(timePeriod) > 1 ? 's' : ''}`
                            : planType?.charAt(0).toUpperCase() + planType?.slice(1);
                          
                          return (
                            <div
                              key={index}
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "16px",
                                padding: "24px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                                border: "1px solid #e9ecef",
                                position: "relative",
                                transition: "all 0.3s ease",
                                display: "flex",
                                flexDirection: "column",
                                minHeight: "280px"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.12)";
                                e.currentTarget.style.transform = "translateY(-4px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
                                e.currentTarget.style.transform = "translateY(0)";
                              }}>
                              
                              {/* Status Badges */}
                              <div style={{
                                position: "absolute",
                                top: "16px",
                                right: "16px",
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                                flexWrap: "wrap",
                                justifyContent: "flex-end"
                              }}>
                                {isDeprecated ? (
                                  <span style={{
                                    padding: "4px 12px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    backgroundColor: "#fff3cd",
                                    color: "#856404"
                                  }}>
                                    Deprecated
                                  </span>
                                ) : (
                                  <span style={{
                                    padding: "4px 12px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    backgroundColor: isSuspended === false ? "#d4edda" : "#f8d7da",
                                    color: isSuspended === false ? "#155724" : "#721c24"
                                  }}>
                                    {isSuspended === false ? "Active" : "Suspended"}
                                  </span>
                                )}
                                {/* Delete button - hidden for deprecated plans */}
                                {!isDeprecated && (
                                  <button
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      padding: "4px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "50%",
                                      width: "32px",
                                      height: "32px",
                                      transition: "background-color 0.2s"
                                    }}
                                    onClick={() => handleDeleteCoupon(planId)}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fee"}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                    title="Delete Plan">
                                    <DeleteSvg color="#dc3545" />
                                  </button>
                                )}
                              </div>

                              {/* Plan Header */}
                              <div style={{ marginBottom: "20px", marginTop: "8px" }}>
                                <h3 style={{
                                  fontSize: "24px",
                                  fontWeight: "700",
                                  color: "#2c3e50",
                                  marginBottom: "8px",
                                  textTransform: "capitalize",
                                  wordBreak: "break-word"
                                }}>
                                  {planTitle}
                                </h3>
                                <div style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: "4px",
                                  marginBottom: "4px"
                                }}>
                                  <span style={{
                                    fontSize: "36px",
                                    fontWeight: "700",
                                    color: "#72bcb8"
                                  }}>
                                    £{totalPrice}
                                  </span>
                                  <span style={{
                                    fontSize: "16px",
                                    color: "#6c757d",
                                    fontWeight: "500"
                                  }}>
                                    / {planDuration}
                                  </span>
                                </div>
                              </div>

                              {/* Plan Details */}
                              <div style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                                marginBottom: "20px"
                              }}>
                                <div style={{
                                  padding: "12px",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <span style={{ fontSize: "14px", color: "#6c757d" }}>Base Price:</span>
                                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#2c3e50" }}>
                                    £{basePrice}
                                  </span>
                                </div>
                                <div style={{
                                  padding: "12px",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <span style={{ fontSize: "14px", color: "#6c757d" }}>Tax:</span>
                                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#2c3e50" }}>
                                    £{taxPrice}
                                  </span>
                                </div>
                                <div style={{
                                  padding: "12px",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <span style={{ fontSize: "14px", color: "#6c757d" }}>Billing:</span>
                                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#2c3e50", textTransform: "capitalize" }}>
                                    {planDuration}
                                  </span>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                                marginTop: "auto"
                              }}>
                                {!isDeprecated && (
                                  <div style={{
                                    display: "flex",
                                    gap: "12px"
                                  }}>
                                    <button
                                      onClick={() => handleToggleClick(planId, items?.suspended)}
                                      style={{
                                        flex: 1,
                                        padding: "12px 16px",
                                        backgroundColor: items?.suspended === false ? "#6c757d" : "#72bcb8",
                                        color: "#ffffff",
                                        border: "none",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease"
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = "0.9";
                                        e.currentTarget.style.transform = "scale(1.02)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = "1";
                                        e.currentTarget.style.transform = "scale(1)";
                                      }}>
                                      {items?.suspended === false ? "Suspend" : "Activate"}
                                    </button>
                                    <button
                                      onClick={() => handleDeprecatePlan(planId)}
                                      disabled={isSuspended}
                                      style={{
                                        flex: 1,
                                        padding: "12px 16px",
                                        backgroundColor: isSuspended ? "#e9ecef" : "#ffc107",
                                        color: isSuspended ? "#6c757d" : "#ffffff",
                                        border: "none",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        cursor: isSuspended ? "not-allowed" : "pointer",
                                        transition: "all 0.2s ease",
                                        opacity: isSuspended ? 0.6 : 1
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSuspended) {
                                          e.currentTarget.style.opacity = "0.9";
                                          e.currentTarget.style.transform = "scale(1.02)";
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSuspended) {
                                          e.currentTarget.style.opacity = "1";
                                          e.currentTarget.style.transform = "scale(1)";
                                        }
                                      }}
                                      title={isSuspended ? "Activate the plan first before deprecating" : "Deprecate this plan permanently"}>
                                      Deprecate
                                    </button>
                                  </div>
                                )}
                                {isDeprecated && (
                                  <div style={{
                                    padding: "8px 12px",
                                    backgroundColor: "#fff3cd",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    color: "#856404",
                                    textAlign: "center"
                                  }}>
                                    ⚠️ Not available for new subscriptions
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }
                      )}
                      </div>
                    )}
                    
                    {/* Empty State for Filtered Results */}
                    {(() => {
                      const plans = data?.data?.subscriptionPlans || data?.data?.subscription_plans || [];
                      const filteredPlans = plans.filter((items: any) => {
                        const isDeprecated = items.deprecated ?? false;
                        const isSuspended = items.suspended ?? false;
                        
                        if (planFilter === "active") return !isDeprecated && !isSuspended;
                        if (planFilter === "suspended") return isSuspended && !isDeprecated;
                        if (planFilter === "deprecated") return isDeprecated;
                        return true;
                      });
                      
                      if (filteredPlans.length === 0 && plans.length > 0) {
                        const filterLabels = {
                          active: { icon: "✓", title: "Active", message: "No active plans found. All plans are either suspended or deprecated." },
                          suspended: { icon: "⏸️", title: "Suspended", message: "No suspended plans. Plans that are temporarily unavailable will appear here." },
                          deprecated: { icon: "⚠️", title: "Deprecated", message: "No deprecated plans yet. Plans marked as deprecated will appear here." },
                          all: { icon: "📋", title: "", message: "No plans available." }
                        };
                        
                        const filterInfo = filterLabels[planFilter as keyof typeof filterLabels];
                        
                        return (
                          <div style={{
                            textAlign: "center",
                            padding: "60px 20px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "12px",
                            marginTop: "20px"
                          }}>
                            <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                              {filterInfo.icon}
                            </div>
                            <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#2c3e50", marginBottom: "8px" }}>
                              No {filterInfo.title} Plans Found
                            </h3>
                            <p style={{ fontSize: "14px", color: "#6c757d", marginBottom: "20px" }}>
                              {filterInfo.message}
                            </p>
                            {planFilter !== "all" && (
                              <button
                                onClick={() => setPlanFilter("all")}
                                style={{
                                  padding: "10px 20px",
                                  backgroundColor: "#72bcb8",
                                  color: "#ffffff",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  cursor: "pointer"
                                }}
                              >
                                View All Plans
                              </button>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {data?.data?.length === 0 || isError === true ? (
                    <NoRecordFound />
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        </div>
      </CustomLaoder>
      <SubscriptionModal
        handleSubmit={() => {
          handleSubmit();
          taxHandleSubmit();
        }}
        modalTitle={modalData.modalTitle}
        open={modalData.open}
        handleCloseModal={handleCloseModal}>
        {returnChild()}
      </SubscriptionModal>
    </div>
  );
};

export default SubscriptionManagementScreen;
